import {FunctionComponent, useMemo, useRef} from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import {twMerge} from 'tailwind-merge';
import ProfileMenu from '@/components/profile-menu';
import {TrackedButton} from '@/components/tracked-atoms/tracked-button';
import {useNetworkStatus} from '@/contexts/network-status-context';
import {useFormatFullName} from '@/hooks/use-format-fullname';
import useShowMenu from '@/hooks/use-show-menu';
import {UserProfile} from '@/repositories/beneva-api/endpoints/users';
import {TrackingClickPrefix} from '@/services/analytics/analytics-types';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';

interface ElementProps {
  profile?: UserProfile;
}

const Profile: FunctionComponent<ElementProps> = ({profile}) => {
  const {t} = useTranslation();
  const {isOffline} = useNetworkStatus();
  const {isMenuVisible, onShowMenu, onCloseMenu} = useShowMenu();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const formatFullName = useFormatFullName();

  const profileFilled = isProfileFilled(profile);

  const fullName = useMemo(() => {
    if (profileFilled) {
      return formatFullName(profile.cons2_firstName, profile.cons1_lastName, {format: 'shortenName'});
    }
    return ' ';
  }, [profileFilled, profile, formatFullName]);

  const code = useMemo(() => {
    if (profileFilled) {
      return `${profile.cons9_generalAgentCode}–${profile.cons25_advisorCode}`;
    }
    return ' ';
  }, [profileFilled, profile]);

  const initials = useMemo(() => {
    if (profileFilled) {
      return `${profile.cons2_firstName[0]}${profile.cons1_lastName[0]}`;
    }
    return '?';
  }, [profileFilled, profile]);

  return (
    <div>
      <TrackedButton
        data-testid="profile-toggle"
        type="button"
        ref={buttonRef}
        className={twMerge(
          clsx('group ml-auto flex flex-row items-center gap-2', isOffline && 'cursor-not-allowed opacity-20'),
        )}
        onClick={onShowMenu}
        aria-label={t('components.application-header.profile.accessibility-button')}
        trackingKey={WEB_CLICK_TRACKING_KEY.OPEN_PROFILE_MENU}
        trackingAs={TrackingClickPrefix.CONTENT}
        trackingText=""
      >
        <NameCodeIndicator name={fullName} code={code} />
        <InitialsPill initials={initials} />
      </TrackedButton>

      <ProfileMenu isVisible={isMenuVisible} onClose={onCloseMenu} toggleRef={buttonRef} />
    </div>
  );
};

type NameCodeIndicatorProps = {
  name: string;
  code: string;
};

const NameCodeIndicator = ({name, code}: NameCodeIndicatorProps) => (
  <div className="flex flex-col items-end">
    <div data-testid="profile-name-indicator" className="text-xs font-semibold text-violet">
      {name}
    </div>
    <div data-testid="profile-code-indicator" className="text-xs text-grey">
      {code}
    </div>
  </div>
);

type InitialsPillProps = {
  initials: string;
};

const InitialsPill = ({initials}: InitialsPillProps) => (
  <div
    data-testid="profile-initials-pill"
    className="flex size-10 items-center justify-center rounded-4 bg-yellow text-base font-medium text-violet"
  >
    {initials}
  </div>
);

const isProfileFilled = (profile: UserProfile | undefined): profile is UserProfile =>
  !!profile && !!profile.cons2_firstName && !!profile.cons1_lastName;

export default Profile;
